// src/components/Home/v2/WebsiteTemplatesHeroAnimation.tsx
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import Text from "@/components/Home/v2/shared/Text";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import CtaButtonGroup, {
  BookADemoSourceEnum,
  CtaActionType,
  SignUpSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";

const SIGN_UP_URL = "/signup";

const ANIM_BASE_DESKTOP = "/images/images/Website Templates/Animation/Desktop";
const ANIM_BASE_MOBILE = "/images/images/Website Templates/Animation/Mobile";

type AnimationTemplate = {
  name: string;
  query: string;
  desktopSvg: string;
  mobileSvg: string;
  gradient: string; // CSS background-image value
};

const ANIMATION_TEMPLATES: AnimationTemplate[] = [
  {
    name: "Culinary Website",
    query: "Online cooking classes website",
    desktopSvg: `${ANIM_BASE_DESKTOP}/Culinery Website.svg`,
    mobileSvg: `${ANIM_BASE_MOBILE}/Culinery Website.svg`,
    gradient:
      "linear-gradient(135deg, #f0e8ff 0%, #ffd4d4 40%, #ffe0cc 70%, #ffc8b4 100%)",
  },
  {
    name: "Interior Design Website",
    query: "Minimal modern interior design website",
    desktopSvg: `${ANIM_BASE_DESKTOP}/Interior Design Website.svg`,
    mobileSvg: `${ANIM_BASE_MOBILE}/Interior Design Website.svg`,
    gradient:
      "linear-gradient(135deg, #f5e6d8 0%, #ffcfd4 40%, #ffb7c5 70%, #ffa0b8 100%)",
  },
  {
    name: "Education Website",
    query: "Professional website for my EdTech startup",
    desktopSvg: `${ANIM_BASE_DESKTOP}/Education Website.svg`,
    mobileSvg: `${ANIM_BASE_MOBILE}/Education Website.svg`,
    gradient:
      "linear-gradient(135deg, #e6eefa 0%, #b8d4ef 40%, #6fa9e6 70%, #4f8ed9 100%)",
  },
];

const SLIDE_MS = 700;

const usePrefersReducedMotion = () => {
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const listener = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);

  return reduce;
};

type GradientBackdropProps = {
  gradient: string;
};

const GradientBackdrop = ({ gradient }: GradientBackdropProps) => {
  const reduce = usePrefersReducedMotion();
  const [layers, setLayers] = useState<{ a: string; b: string; active: "a" | "b" }>({
    a: gradient,
    b: gradient,
    active: "a",
  });

  useEffect(() => {
    setLayers((prev) => {
      if (prev.active === "a") return { a: prev.a, b: gradient, active: "b" };
      return { a: gradient, b: prev.b, active: "a" };
    });
  }, [gradient]);

  const transitionClass = reduce ? "" : "transition-opacity duration-[600ms] ease-in-out";

  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl">
      <div
        className={`absolute inset-0 ${transitionClass}`}
        style={{ backgroundImage: layers.a, opacity: layers.active === "a" ? 1 : 0 }}
      />
      <div
        className={`absolute inset-0 ${transitionClass}`}
        style={{ backgroundImage: layers.b, opacity: layers.active === "b" ? 1 : 0 }}
      />
    </div>
  );
};

type TypewriterPromptBarProps = {
  queries: string[];
  onQueryChange: (index: number) => void;
};

const TypewriterPromptBar = ({ queries, onQueryChange }: TypewriterPromptBarProps) => {
  const [displayText, setDisplayText] = useState("");
  const [queryIdx, setQueryIdx] = useState(0);
  const [phase, setPhase] = useState<"typing" | "pausing" | "deleting">("typing");
  const frameRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Notify parent whenever queryIdx changes (including the initial mount)
  useEffect(() => {
    onQueryChange(queryIdx);
  }, [queryIdx, onQueryChange]);

  useEffect(() => {
    const query = queries[queryIdx];

    if (phase === "typing") {
      if (displayText.length < query.length) {
        frameRef.current = setTimeout(() => {
          setDisplayText(query.slice(0, displayText.length + 1));
        }, 55);
      } else {
        frameRef.current = setTimeout(() => setPhase("pausing"), 1800);
      }
    } else if (phase === "pausing") {
      frameRef.current = setTimeout(() => setPhase("deleting"), 0);
    } else {
      if (displayText.length > 0) {
        frameRef.current = setTimeout(() => {
          setDisplayText((t) => t.slice(0, -1));
        }, 28);
      } else {
        setQueryIdx((i) => (i + 1) % queries.length);
        setPhase("typing");
      }
    }

    return () => {
      if (frameRef.current) clearTimeout(frameRef.current);
    };
  }, [displayText, phase, queryIdx, queries]);

  return (
    <div className="flex w-full max-w-[640px] items-center gap-2 rounded-full border border-white/60 bg-white/95 p-1.5 pl-2 shadow-sm backdrop-blur-sm">
      <span className="flex-1 truncate px-4 py-2.5 text-[15px] text-neutralBlack/80">
        {displayText}
        <span className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] motion-safe:animate-pulse bg-[#5766F2]" />
      </span>
      <Link
        href={SIGN_UP_URL}
        aria-label="Start your free trial"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#5766F2] px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 md:px-6 md:py-2.5"
      >
        <span className="hidden md:inline">Go</span>
        <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
};

type VerticalSlideProps = {
  templates: AnimationTemplate[];
  activeIndex: number;
  variant: "desktop" | "mobile";
};

const VerticalSlide = ({ templates, activeIndex, variant }: VerticalSlideProps) => {
  const reduce = usePrefersReducedMotion();
  const sizes =
    variant === "desktop"
      ? "(max-width: 1200px) 100vw, 1100px"
      : "(max-width: 375px) 92vw, 360px";

  return (
    <div className="relative mx-auto h-full w-full overflow-hidden">
      {templates.map((template, i) => {
        const isActive = i === activeIndex;
        const svg = variant === "desktop" ? template.desktopSvg : template.mobileSvg;
        const transform = isActive ? "translateY(0%)" : "translateY(105%)";
        return (
          <div
            key={template.name}
            aria-hidden={!isActive}
            className="absolute inset-x-0 bottom-0 overflow-hidden rounded-t-2xl"
            style={{
              height: "100%",
              transform,
              opacity: isActive ? 1 : 0,
              transition: reduce
                ? "none"
                : `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${SLIDE_MS}ms ease-out`,
              willChange: "transform, opacity",
            }}
          >
            <Image
              src={svg}
              alt={template.name}
              fill
              sizes={sizes}
              className="object-cover object-top"
              priority={i === 0}
            />
          </div>
        );
      })}
    </div>
  );
};

const WebsiteTemplatesHero = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const activeTemplate = ANIMATION_TEMPLATES[activeIdx];

  return (
    <section className="w-full bg-white pb-16 pt-12 md:pb-24 md:pt-16">
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <Text as="h1" variant="display-lg" className="text-neutralBlack">
            Build Your Website in Minutes
          </Text>
          <Text as="p" variant="body-lg" className="mt-4 text-darkSlate04">
            Choose a ready-made template, customize it your way, and launch
            without writing a single line of code.
          </Text>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <CtaButtonGroup
              ctas={[
                {
                  type: ButtonTypeEnum.Primary,
                  size: ButtonSizeEnum.Regular,
                  text: "Start Your Free Trial",
                  actionType: CtaActionType.SignUp,
                  amplitudeEvent: {
                    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
                    eventProperties: { source: SignUpSourceEnum.hero },
                  },
                },
                {
                  type: ButtonTypeEnum.Secondary,
                  size: ButtonSizeEnum.Regular,
                  text: "Book A Demo",
                  actionType: CtaActionType.BookDemo,
                  amplitudeEvent: {
                    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                    eventProperties: { source: BookADemoSourceEnum.hero },
                  },
                },
              ]}
              gap="flex gap-3"
              fullWidthOnMobile={false}
            />
          </div>
        </div>

        {/* Gradient card — width matches navbar (max-w-6xl) */}
        <div className="relative isolate mx-auto mt-10 w-full max-w-6xl overflow-hidden rounded-3xl px-6 pb-0 pt-8 md:px-10 md:pt-10">
          <GradientBackdrop gradient={activeTemplate.gradient} />

          <div className="flex justify-center">
            <TypewriterPromptBar
              queries={ANIMATION_TEMPLATES.map((t) => t.query)}
              onQueryChange={setActiveIdx}
            />
          </div>

          {/* Desktop slide */}
          <div className="relative mx-auto mt-8 hidden h-[480px] w-full md:block">
            <VerticalSlide
              templates={ANIMATION_TEMPLATES}
              activeIndex={activeIdx}
              variant="desktop"
            />
          </div>

          {/* Mobile slide */}
          <div className="relative mx-auto mt-6 h-[340px] w-full md:hidden">
            <VerticalSlide
              templates={ANIMATION_TEMPLATES}
              activeIndex={activeIdx}
              variant="mobile"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebsiteTemplatesHero;
